import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Heart, Loader2, Sparkles, Download, Printer, History, Trash2 } from "lucide-react";
import { formatApiError, API_BASE } from "@/lib/api";
import CitySearchInput from "@/components/CitySearchInput";
import { useNavigate } from "react-router-dom";

export default function KundliMilan() {
  const [boy, setBoy] = useState({ name: "", dob: "", tob: "", place_of_birth: "" });
  const [girl, setGirl] = useState({ name: "", dob: "", tob: "", place_of_birth: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const nav = useNavigate();

  const run = async () => {
    if (!boy.dob || !girl.dob) return toast.error("Both dates of birth required");
    setBusy(true);
    try {
      const { data } = await api.post("/kundli/milan", { boy, girl, with_ai: true, save: true });
      setResult(data);
      toast.success("Saved to Milan history");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };

  const downloadPdf = async () => {
    if (!result) return;
    setPdfBusy(true);
    try {
      const t = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/kundli/milan/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: "include",
        body: JSON.stringify(result.record_id ? { record_id: result.record_id } : { result, ai_summary: result.ai_summary }),
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(result.boy.name || "Groom")}_${(result.girl.name || "Bride")}_Milan.pdf`.replace(/\s+/g, "_");
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Milan PDF downloaded");
    } catch { toast.error("PDF failed"); }
    setPdfBusy(false);
  };

  const printPage = () => window.print();

  const pct = result ? (result.total / result.max) * 100 : 0;
  const color = pct >= 78 ? "emerald" : pct >= 66 ? "amber" : "red";

  return (
    <div className="space-y-6" data-testid="kundli-milan-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Ashtakoot</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1 flex items-center gap-2">
            <Heart className="w-7 h-7 text-rose-500" strokeWidth={1.5} /> Kundli Milan
          </h1>
          <p className="text-sm text-slate-500 mt-1">Vedic 8-koota compatibility scoring out of 36 gunas.</p>
        </div>
        <Button variant="outline" onClick={() => nav("/kundli-milan/history")} data-testid="milan-history-btn" className="btn-pill">
          <History className="w-4 h-4 mr-1.5" /> History
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PersonCard title="Groom" data={boy} setData={setBoy} testid="milan-boy" />
        <PersonCard title="Bride" data={girl} setData={setGirl} testid="milan-girl" />
      </div>

      <Button onClick={run} disabled={busy} className="bg-[#1E3A8A] btn-pill h-12 px-8" data-testid="milan-calculate">
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Heart className="w-4 h-4 mr-1.5" />} Calculate compatibility
      </Button>

      {result && (
        <div className="space-y-6" data-testid="milan-result">
          <div className="flex flex-wrap gap-2 justify-end items-center print:hidden">
            {result.serial_no && <span className="font-mono text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800" data-testid="milan-serial">Report No. {result.serial_no}</span>}
            <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy} data-testid="milan-pdf-btn">
              {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Download PDF
            </Button>
            <Button variant="outline" onClick={printPage} data-testid="milan-print-btn">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
          </div>

          {/* Score dial */}
          <Card className={`p-8 rounded-2xl text-center bg-gradient-to-br ${
            color === "emerald" ? "from-emerald-50 to-transparent dark:from-emerald-500/10" :
            color === "amber" ? "from-amber-50 to-transparent dark:from-amber-500/10" :
            "from-red-50 to-transparent dark:from-red-500/10"
          } border-slate-200 dark:border-slate-800`}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Total Gunas Matched</div>
            <div className="font-display text-7xl md:text-8xl font-light mt-2 tracking-tight">
              <span className={color === "emerald" ? "text-emerald-600 dark:text-emerald-400" : color === "amber" ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}>
                {result.total}
              </span>
              <span className="text-slate-400 text-4xl md:text-5xl"> / {result.max}</span>
            </div>
            <Badge className={`mt-4 ${
              color === "emerald" ? "bg-emerald-100 text-emerald-800" :
              color === "amber" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"
            }`}>{result.verdict}</Badge>
            <div className="mt-6 grid grid-cols-2 gap-6 text-sm max-w-md mx-auto">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Groom</div>
                <div className="font-medium">{result.boy.name || "—"}</div>
                <div className="text-xs text-slate-500">{result.boy.rashi} · {result.boy.nakshatra}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Bride</div>
                <div className="font-medium">{result.girl.name || "—"}</div>
                <div className="text-xs text-slate-500">{result.girl.rashi} · {result.girl.nakshatra}</div>
              </div>
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800">
            <h3 className="font-display text-xl mb-4">Ashtakoot Breakdown</h3>
            <div className="space-y-3">
              {result.gunas.map(g => {
                const p = (g.got / g.max) * 100;
                return (
                  <div key={g.name} data-testid={`guna-${g.name.toLowerCase().replace(' ','-')}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium">{g.name} <span className="text-xs text-slate-500">— {g.meaning}</span></span>
                      <span className="font-mono">{g.got} / {g.max}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full ${p >= 66 ? "bg-emerald-500" : p >= 33 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${p}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {result.ai_summary && (
            <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-gradient-to-br from-amber-50/40 to-transparent dark:from-amber-500/5" data-testid="milan-ai-summary">
              <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Astrologer's Interpretation</h3>
              <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{result.ai_summary}</div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

const PersonCard = ({ title, data, setData, testid }) => (
  <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-amber-600 dark:text-amber-400">{title}</div>
    <h3 className="font-display text-2xl mt-1 mb-4">{data.name || title + " details"}</h3>
    <div className="grid grid-cols-1 gap-3">
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">Name</Label><Input value={data.name} onChange={e=>setData({...data,name:e.target.value})} className="mt-1" data-testid={`${testid}-name`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-widest text-slate-500">DOB *</Label><Input type="date" value={data.dob} onChange={e=>setData({...data,dob:e.target.value})} className="mt-1" data-testid={`${testid}-dob`} /></div>
        <div><Label className="text-xs uppercase tracking-widest text-slate-500">TOB</Label><Input type="time" value={data.tob} onChange={e=>setData({...data,tob:e.target.value})} className="mt-1" /></div>
      </div>
      <div><Label className="text-xs uppercase tracking-widest text-slate-500">Place of birth</Label>
        <div className="mt-1">
          <CitySearchInput
            value={data.place_of_birth}
            onChange={(v) => setData({ ...data, place_of_birth: v })}
            onSelect={(c) => setData({ ...data, place_of_birth: c.label, lat: c.lat, lon: c.lon, tz: c.timezone })}
            placeholder="Type any city, hamlet or town…"
            testid={`${testid}-place`}
          />
        </div>
      </div>
      <details className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-3" data-testid={`${testid}-manual-latlon`}>
        <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-slate-500 select-none">
          Place not found? Enter latitude / longitude manually
        </summary>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Input type="number" step="0.0001" value={data.lat || ""} onChange={e=>setData({...data, lat: e.target.value})}
                 placeholder="Lat (e.g. 20.2961)" className="h-9 rounded-lg text-xs" data-testid={`${testid}-lat`} />
          <Input type="number" step="0.0001" value={data.lon || ""} onChange={e=>setData({...data, lon: e.target.value})}
                 placeholder="Lon (e.g. 85.8245)" className="h-9 rounded-lg text-xs" data-testid={`${testid}-lon`} />
          <Input value={data.tz || ""} onChange={e=>setData({...data, tz: e.target.value})}
                 placeholder="TZ (auto)" className="h-9 rounded-lg text-xs" data-testid={`${testid}-tz`} />
        </div>
      </details>
    </div>
  </Card>
);
